import { type FC, useContext } from "react";

import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type WeightData } from "~/domains/product/types/WeightData";

import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import Spacer from "~/common/components/Spacer";
import * as WeightUtils from "~/utils/WeightUtils";
import * as PriceUtils from "~/utils/PriceUtils";
import { connect, useDispatch, useSelector } from "react-redux";
import ProductTable from "~/features/cart/activeQuote/components/productTable/ProductTable";
import { selectTargetSubtotal } from "~/features/cart/activeQuote/redux/selectors";
import { type CartGrouping, type LineItem } from "~/features/cart/cart/models/CartData";
import { type RootState } from "~/app/store";
import { selectSubtotal } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "~/utils/url/urlResolver";
import { selectLineItem, selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { Button, Stack, Typography } from "~/common/mui";
import { selectSalesmanCheckoutCart } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import CheckoutSectionTitle from "~/features/cart/checkout/components/CheckoutSectionTitle";
import { requestConfirmation } from "~/common/utils/ModalService";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";

import classes from "~/features/cart/activeQuote/components/ActiveQuoteReview/ActiveQuoteReview.module.less";
import { ActiveQuotePageModeContext } from "~/features/cart/activeQuote/components/ActiveQuotePage/ActiveQuotePage";
import { Mode } from "~/features/cart/cart/models/QuoteData";
import { scrollToTop } from "~/common/utils/misc";
import { Alert } from "~/common/mui/Alert/Alert";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { AnyAction } from "~/app/store/types";
import type { ThunkAction } from "redux-thunk";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";

interface FromStateProps {
  requestInFlight: boolean;
  accountNumber: string;
  totalWeight: WeightData;
  totalSum: PriceData;
  totalTargetSum: PriceData;

  validCart: boolean;
}

type Props = FromStateProps;

const ActiveQuoteReview: FC<Props> = (props) => {
  const { totalWeight, totalSum, validCart } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const cartData = useSelector(selectSalesmanCheckoutCart);
  const setMode = useContext(ActiveQuotePageModeContext);

  const fccEntries = cartData.orderables;
  const nonFccEntries = cartData.quotables;

  const isMixedFcc = fccEntries.entries.length > 0 && nonFccEntries.entries.length > 0;

  const trackPlaceOrder = (isFccOnly: boolean) => {
    trackFccAction(isFccOnly ? FccAction.CreateQuoteForFcc : FccAction.CreateQuoteForAll);
    dispatch(trackPlaceOrderThunk(isFccOnly));
  };

  const submitOnlyFcc = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = e.currentTarget.href;
    const confirmation = await requestConfirmation(({ onConfirm, onCancel }) => (
      <ConfirmationModal
        label={
          <Stack gap={4}>
            <Typography variant={"bodyMedium"}>
              <b>{t("fcc.checkout.confirmation.title")}</b>
            </Typography>
            <Typography variant={"bodyMedium"}>{t("fcc.checkout.confirmation.content")}</Typography>
          </Stack>
        }
        okLabel={t("fcc.checkout.confirmation.yes")}
        cancelLabel={t("fcc.checkout.confirmation.no")}
        onCancel={onCancel}
        onOk={onConfirm}
      />
    ));
    if (confirmation) {
      trackPlaceOrder(true);
      window.location.href = url;
    } else {
      setMode(Mode.EDIT);
      scrollToTop();
    }
  };

  const buttons = (
    <>
      {isMixedFcc && (
        <Button
          data-test-id={"btn-place-quote-fcc-only"}
          color={"secondary"}
          disabled={!validCart}
          // @ts-ignore
          onClick={submitOnlyFcc}
          href={resolveUrl("/quote/confirm?fccOnly=true")}
        >
          {t("fcc.checkout.btn.fccOnly")}
        </Button>
      )}
      <Button
        data-test-id={"btn-place-quote"}
        disabled={!validCart}
        href={resolveUrl("/quote/confirm")}
        onClick={() => trackPlaceOrder(false)}
      >
        {t(isMixedFcc ? "fcc.checkout.btn.mixed" : "activeQuotePage.review.send")}
      </Button>
    </>
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }} className={"mb-30"}>
        <Alert severity={"info"}>{t("activeQuotePage.review.summary")}</Alert>
        <Stack direction={"row"} gap={2}>
          {buttons}
        </Stack>
      </div>

      {fccEntries.entries.length > 0 && (
        <>
          <CheckoutSectionTitle>
            {`Items to be requested - Free Capacity scope (${fccEntries.entries.length})`}
          </CheckoutSectionTitle>
          <ProductTable group={fccEntries} editMode={false} />
        </>
      )}
      {nonFccEntries.entries.length > 0 && (
        <>
          <CheckoutSectionTitle>
            {t("cartPage.header.toBeQuoted", { count: nonFccEntries.entries.length })}
          </CheckoutSectionTitle>
          <ProductTable group={nonFccEntries} editMode={false} />
        </>
      )}

      <Spacer isThick={true} className={"mb-30 mt-60"} />
      <div className="row">
        <div className="col-push-6 col-xl-push-8 col-6 col-xl-4">
          <OrderTotals showPriceOnRequest={false} weight={totalWeight} totalValue={totalSum}>
            <Stack gap={4} className={isMixedFcc ? classes.bottomButtonContainerOuter : undefined}>
              <Stack direction={"row"} gap={2} className={isMixedFcc ? classes.bottomButtonContainer : undefined}>
                {buttons}
              </Stack>
              {isMixedFcc && <Alert severity={"info"}>{t("fcc.checkout.mixedMessage")}</Alert>}
            </Stack>
          </OrderTotals>
        </div>
      </div>

      <div className="d-flex flex-direction-column">
        <Alert className={"mt-30"} severity="info">
          {t("activeQuotePage.review.footerMsg1")}
        </Alert>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState): FromStateProps => {
  if (state.cartPage.accountNumber === "") {
    return {
      requestInFlight: false,
      accountNumber: "",
      totalWeight: WeightUtils.zero(),
      totalSum: PriceUtils.zero(),
      totalTargetSum: PriceUtils.zero(),
      validCart: true,
    };
  }

  const { cartPage: pageData, cart: cartData } = state;
  const packEntries: LineItem[] = Object.values(cartData.packEntriesById);

  const productCodes = Object.keys(selectLineItemsByProduct(state));

  const totalsPerProduct = productCodes.map((productCode) =>
    selectSubtotal(state, {
      productCode: productCode,
      isInCheckout: false,
    }),
  );
  const totalPrice = PriceUtils.volatileSum(...totalsPerProduct);

  const targetTotalsPerProduct = productCodes.map((productCode) => selectTargetSubtotal(state, productCode));
  const targeTotalPrice = PriceUtils.volatileSum(...targetTotalsPerProduct);

  const weights = packEntries.map((entry: LineItem) => WeightUtils.multiply(entry.weight, entry.quantity));
  const weight = WeightUtils.sum(...weights);

  return {
    accountNumber: pageData.accountNumber,
    totalWeight: weight,
    totalSum: totalPrice,
    totalTargetSum: targeTotalPrice,
    validCart: packEntries.reduce((isValid, entry) => isValid && entry.valid, true as boolean),
    requestInFlight: pageData.requestInFlight,
  };
};

// TODO: Wait for https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363 to be fixed
// @ts-ignore
export default connect(mapStateToProps)(ActiveQuoteReview);

function trackPlaceOrderThunk(isFccOnly: boolean): ThunkAction<void, RootState, undefined, AnyAction> {
  return (_, getState) => {
    const state = getState();

    const cartData = selectSalesmanCheckoutCart(state);

    function getPackEntryIds(group: CartGrouping) {
      return group.entries.flatMap((entry) => entry.packGroups).flatMap((entry) => entry.packEntryIds);
    }
    // orderables = fcc items
    // quotables = non fcc items
    const packEntryIds = getPackEntryIds(cartData.orderables);
    if (!isFccOnly) {
      packEntryIds.push(...getPackEntryIds(cartData.quotables));
    }
    const entries = packEntryIds.map((id) => {
      const lineItem = selectLineItem(id)(state);
      const product = selectProductDescriptor(state, lineItem);
      return {
        lineItem,
        product,
      };
    });

    AnalyticsMQ.instance.push("placeOrder", {
      orderId: "N/A",
      type: "salesman-quote",
      paymentType: "",
      entries,
    });
  };
}
